import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, ArrowRight, Users2, Compass, BookOpen } from "lucide-react";
import { useAuth } from "@/store/AuthContext";
import { useMySegmentation, useSaveSegmentation, useRecomputeMatch, useMarkOnboarded } from "@/lib/hooks";
import { SEGMENTATION_STEPS as STEPS, DEFAULT_SEGMENTATION as DEFAULT_ANSWERS } from "@/lib/segmentation";

export default function Onboarding() {
  const nav = useNavigate();
  const { user, login, checkAuth } = useAuth();
  const { data: remoteSegmentation } = useMySegmentation();
  const saveMut = useSaveSegmentation();
  const matchMut = useRecomputeMatch();
  const markOnboarded = useMarkOnboarded();

  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState(DEFAULT_ANSWERS);

  useEffect(() => {
    if (remoteSegmentation) {
      setAnswers((prev) => ({ ...prev, ...remoteSegmentation }));
    }
  }, [remoteSegmentation]);

  const step = STEPS[i];
  const pick = (v) => setAnswers((a) => ({ ...a, [step.id]: v }));

  const showWelcomeToast = (matchScore, groupName) => {
    toast.custom(
      (t) => (
        <div className="rounded-2xl border border-stone-200 dark:border-[#3a2f22] bg-white dark:bg-[#221c17] text-stone-900 dark:text-[#E8D8C4] p-5 shadow-xl w-[360px] font-sans" data-testid="welcome-toast">
          <p className="font-serif text-xl leading-tight">Welcome to Cadence.</p>
          <p className="text-sm text-stone-600 dark:text-[#A6947A] mt-1">
            You matched into <span className="text-terracotta">{groupName}</span> at <span className="data-mono">{matchScore}%</span>. Start here:
          </p>
          <div className="mt-4 grid grid-cols-3 gap-2">
            <button onClick={() => { toast.dismiss(t); nav("/library"); }} className="btn-outline text-xs !min-h-[2.5rem] flex-col !py-2" data-testid="welcome-cta-library">
              <BookOpen size={14} /> Library
            </button>
            <button onClick={() => { toast.dismiss(t); nav("/calendar"); }} className="btn-outline text-xs !min-h-[2.5rem] flex-col !py-2" data-testid="welcome-cta-calendar">
              <Compass size={14} /> Calendar
            </button>
            <button onClick={() => { toast.dismiss(t); nav("/community"); }} className="btn-outline text-xs !min-h-[2.5rem] flex-col !py-2" data-testid="welcome-cta-community">
              <Users2 size={14} /> Community
            </button>
          </div>
        </div>
      ),
      { duration: 15000 },
    );
  };

  const next = async () => {
    if (i < STEPS.length - 1) {
      setI(i + 1);
      return;
    }
    if (!user) {
      toast("Sign in to save", {
        description: "We keep your segmentation on your account.",
        action: { label: "Sign in", onClick: () => login() },
      });
      return;
    }
    try {
      await saveMut.mutateAsync(answers);
      const res = await matchMut.mutateAsync();
      const wasFirstTime = user.has_onboarded === false;
      if (wasFirstTime) {
        await markOnboarded.mutateAsync();
        await checkAuth();  // refresh user state so has_onboarded flips true
        showWelcomeToast(res.match_score, res.group_name);
        nav("/");
      } else {
        toast("Match refined", {
          description: `New score: ${res.match_score}% · ${res.group_name}`,
        });
        nav("/community");
      }
    } catch (e) {
      toast.error("Couldn't save", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8" data-testid="onboarding-page">
      <header>
        <span className="eyebrow">Match setup · {i + 1} of {STEPS.length}</span>
        <h1 className="font-serif text-4xl sm:text-5xl text-stone-900 mt-2 leading-tight">
          Tell us who you sit with.
        </h1>
        <p className="text-stone-600 mt-3 leading-relaxed">
          Cadence uses these signals only to find you a circle whose rhythm resembles yours. You can change any of them, any time.
        </p>
        {!user && (
          <p className="text-xs text-stone-500 mt-3">
            You can browse anonymously — but you&apos;ll need to <button onClick={login} className="underline text-terracotta">sign in</button> before we can save your match.
          </p>
        )}
      </header>

      <div className="h-1 rounded-full bg-stone-100 overflow-hidden">
        <div
          className="h-full bg-terracotta transition-all duration-300"
          style={{ width: `${((i + 1) / STEPS.length) * 100}%` }}
        />
      </div>

      <section className="card-editorial p-8 space-y-6" data-testid={`step-${step.id}`}>
        <div>
          <span className="eyebrow">{step.label}</span>
          <h2 className="font-serif text-3xl text-stone-900 mt-1">Pick the closest.</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {step.options.map((o) => {
            const selected = answers[step.id] === o;
            return (
              <button
                key={o}
                onClick={() => pick(o)}
                data-testid={`opt-${step.id}-${o}`}
                className={`rounded-full px-4 py-2 text-sm border transition-colors ${
                  selected
                    ? "bg-charcoal text-paper border-charcoal"
                    : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"
                }`}
              >
                {o}
              </button>
            );
          })}
        </div>
      </section>

      <div className="flex items-center justify-between">
        <button
          className="btn-outline"
          onClick={() => setI((v) => Math.max(0, v - 1))}
          disabled={i === 0}
          data-testid="onboarding-back"
        >
          Back
        </button>
        <button
          className="btn-accent"
          onClick={next}
          disabled={saveMut.isPending || matchMut.isPending}
          data-testid="onboarding-next"
        >
          {i === STEPS.length - 1
            ? (saveMut.isPending || matchMut.isPending ? "Saving…" : "Refine my circle")
            : "Continue"} <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
